import { Injectable } from "@angular/core"
import { FireStoreService } from "@shared/service/fire-store.service"
import { UnitDTO, UnitService } from "@app/pages/unit/unit.service"
import { UnitV1Service } from "@app/pages/unitV1/unit-v1.service"
import { arrayUnion } from "@angular/fire/firestore"

@Injectable({
  providedIn: "root",
})
export class ProgramService extends FireStoreService<ProgramDTO> {
  constructor(
    private unitService: UnitService,
    private unitV1Service: UnitV1Service,
  ) {
    super()
    this.init({
      collectionKey: "programs",
      fullTextFields: ["name"],
      sorting: {
        field: "name",
      },
    })
  }

  beforeSavingHook(dto: any): any {
    if (!dto.versions) return dto // allowing partial update without erasing existing versions...
    return {
      ...dto,
      versions: (dto.versions || []).map((version: any) => ({
        ...version,
        units: (version.units || []).map((unit: any) => this.toUnitLineRef(version, unit)),
      })),
    } // return a clone otherwise there is a blinking
  }

  toDTO(dto: any, action: string): Promise<ProgramDTO> {
    // if (action === FireStoreService.ACTION.GET || action === FireStoreService.ACTION.UPDATE) {
    const promises: Array<Promise<any>> = []
    if (dto.versions) {
      dto.versions.forEach((version: any) =>
        (version?.units || []).forEach((unit: any) => {
          // sans path : pièce déjà hydratée (ex : update partiel repris du cache)
          if (unit?.value?.id && unit.value.path) {
            unit.isV2 = unit.value.path.startsWith("unitsV2/")
            const service = unit.isV2 ? this.unitService : this.unitV1Service
            const { id, path } = unit.value
            promises.push(service.get(id, true).then((value) => (unit.value = value || this.missingUnit(id, path))))
          }
        }),
      )
    }

    return Promise.all([this.unitService.getAllFromCache(), this.unitV1Service.getAllFromCache()])
      .then(() => Promise.all(promises))
      .then(() => dto)
    // } else {
    //   return super.toDTO(dto, action)
    // }
  }

  // pièce référencée par une ligne mais absente de la base : l'id est conservé pour ne pas perdre la référence à l'enregistrement
  private missingUnit(id: string, path: string): UnitDTO {
    return { id, name: "⚠ Pièce introuvable", subtitle: path, type: { id: "" }, versions: [], missing: true }
  }

  // une version v1 est créée avec sa version v2 jumelle (même logique que tools/migrate-programs-v2.js)
  async createVersion(programId: string, version: any): Promise<string> {
    if (version.units) version.units = (version.units || []).map((unit: any) => this.toUnitLineRef(version, unit))

    version.id = this.uuid()
    const versions = version.isV2 ? [version] : [version, await this.buildV2Version(version)]
    return this.firestore
      .collection(this.config.collectionKey)
      .doc(programId)
      .set({ versions: arrayUnion(...versions) }, { merge: true })
      .then(() => version.id)
      .catch((err) => this.handleError(err, ""))
  }

  // mêmes ids de lignes que la version v1 ; une ligne passe en v2 si sa pièce v1 est migrée (unitV2Id existant)
  private async buildV2Version(v1Version: any): Promise<any> {
    const [unitsV1, unitsV2] = await Promise.all([
      this.unitV1Service.getAllFromCache(),
      this.unitService.getAllFromCache(),
    ])
    const unitV2IdByV1Id = new Map(unitsV1.map((u) => [u.id, u.unitV2Id]))
    const unitV2Ids = new Set(unitsV2.map((u) => u.id))
    const v2Version = {
      ...v1Version,
      id: this.uuid(),
      isV2: true,
      sourceVersionId: v1Version.id,
      configurations: [],
      units: (v1Version.units || []).map((line: any) => {
        const unitV2Id = unitV2IdByV1Id.get(line.value.id)
        return unitV2Id && unitV2Ids.has(unitV2Id)
          ? { ...line, isV2: true, value: { id: unitV2Id } }
          : { ...line, isV2: false }
      }),
    }
    v2Version.units = v2Version.units.map((unit: any) => this.toUnitLineRef(v2Version, unit))
    return v2Version
  }

  // une version v2 peut contenir des lignes v1 (pièces pas encore migrées) : la collection est celle de la ligne,
  // sinon (ligne ajoutée depuis l'écran) celle de la version. isV2 de la ligne n'est pas stocké.
  private toUnitLineRef(version: { isV2?: boolean }, unit: any) {
    const { isV2, ...line } = unit
    const collection = isV2 ?? version.isV2 ? "unitsV2/" : "units/"
    return {
      ...line,
      id: unit.id || this.uuid(),
      value: this.firestore.doc(collection + unit.value.id).ref,
    }
  }

  getNewVersionConfiguration(name: string): ProgramConfigurationDTO {
    return { name, id: this.uuid(), unitsConf: {} }
  }
}

export interface ProgramDTO {
  id: string
  name: string
  name_official: string
  versions: Array<ProgramVersionDTO>
}

export interface ProgramVersionDTO {
  id: string
  name: string
  isV2?: boolean
  sourceVersionId?: string // version v1 dont la version v2 est issue
  units: Array<UnitProgramDTO>
  configurations: Array<ProgramConfigurationDTO>
}

export interface ProgramConfigurationDTO {
  id: string
  name: string
  unitsConf: {
    [unitId: string]: string /*unit versionId*/
  }
  partsConf?: {
    [unitId: string]: { [partId: string]: string /*part versionId*/ }
  }
  partPointsConf?: {
    [unitId: string]: { [roleOrPlaceId: string]: string /*PlacePointDTO id*/ }
  }
}

export interface UnitProgramDTO {
  value: UnitDTO /*stored in firestore as a reference!!*/
  id: string
  isV2?: boolean // déduit de la référence (unitsV2/…), non stocké
}
